"use client";

/**
 * Does this user manage at least one festival?
 *
 * Exists because <ClientLayout> renders the mobile menu ABOVE
 * <AuthenticatedProviders>, so it cannot call useApplicationState() to read
 * `organizerEntities` the way the desktop sidebar (VenueManagerNav) does —
 * the provider is a descendant, not an ancestor.
 *
 * ⚠️ Gated on actual ownership, NOT on the `festivalManager` role flag: 8 of
 * the 34 festival owners in production don't have it set, so the flag would
 * hide Festivals from a quarter of the people who need it. Same reasoning as
 * VenueManagerNav. See docs/festivals.md.
 *
 * ⚠️ Both ownership shapes are checked. A festival carries a scalar `owner`
 * AND an `owners` map, and every festival in production was created by the
 * legacy Vue app, which queries the map — reading the scalar alone hides a
 * festival from a co-manager who isn't the scalar owner. Mirrors
 * loadOrganizerEntities and getManagedFestivalIds. A map key set to null is a
 * soft-removed owner, which `!= null` correctly excludes.
 *
 * Cheaper than loadOrganizerEntities, which answers a much broader question
 * with seven queries: this needs only existence, so each query takes limit(1),
 * and the answer is cached per uid for the life of the page session.
 */

import { useEffect, useState } from "react";
import { collection, getDocs, limit, query, where } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { logError } from "@/lib/bugsnag";

/** Resolved answers, keyed by uid. Survives client-side navigation. */
const cache = new Map<string, boolean>();
/** In-flight lookups, so concurrent mounts share one pair of queries. */
const inFlight = new Map<string, Promise<boolean>>();

async function lookup(uid: string): Promise<boolean> {
  const [byOwner, byOwnersMap] = await Promise.all([
    getDocs(query(collection(db, "festivals"), where("owner", "==", uid), limit(1))),
    getDocs(
      query(collection(db, "festivals"), where(`owners.${uid}`, "!=", null), limit(1))
    ),
  ]);
  return !byOwner.empty || !byOwnersMap.empty;
}

export function useHasFestivals(uid: string | null | undefined): boolean {
  const [hasFestivals, setHasFestivals] = useState<boolean>(() =>
    uid ? cache.get(uid) ?? false : false
  );

  useEffect(() => {
    if (!uid) {
      setHasFestivals(false);
      return;
    }

    const cached = cache.get(uid);
    if (cached !== undefined) {
      setHasFestivals(cached);
      return;
    }

    let active = true;
    const pending =
      inFlight.get(uid) ??
      lookup(uid)
        .then((result) => {
          cache.set(uid, result);
          return result;
        })
        .finally(() => {
          inFlight.delete(uid);
        });
    inFlight.set(uid, pending);

    pending
      .then((result) => {
        if (active) setHasFestivals(result);
      })
      .catch((error) => {
        // A menu item is not worth surfacing an error for; hiding it is the
        // safe failure, and the user still reaches /festivals from the desktop
        // sidebar or directly.
        logError(error, { context: "useHasFestivals", uid });
        if (active) setHasFestivals(false);
      });

    return () => {
      active = false;
    };
  }, [uid]);

  return hasFestivals;
}

export default useHasFestivals;
